/**
 * Gesamtuebersicht "Uebertragungen" nach Sketch 025 Variante C (D-03, D-04, D-21, D-23): Seitenkopf
 * mit Jahresauswahl, zweistufiger Filter, Raster, Liste mit wechselnder Ueberschrift und
 * Seitenleiste. Der komplette Filterzustand lebt ausschliesslich in der URL -- kein paralleles
 * `useState` dafuer (Muster `MarketEvaluationPage.tsx`). Jede Aenderung ist eine neue Serveranfrage
 * (D-04); diese Seite filtert nichts selbst nach.
 *
 * Zwei Effekte: die Uebersicht (Raster + Filterzaehler) haengt an year/area/type, die Liste
 * zusaetzlich an cellType/cellPeriod (`<interfaces>` in 41-10-PLAN.md). Fehlt `year` in der URL,
 * bestimmt `parseTransferOverviewSearch` bereits client-seitig einen Vorschlag
 * (`currentReportYear()`, 41-06-Entscheidung); die tatsaechlich vom Server bestaetigte Antwort wird
 * danach per `replace` in die URL geschrieben (kein zusaetzlicher History-Eintrag). Die Liste laedt
 * erst, sobald `year` tatsaechlich in der URL steht -- sonst liefe sie einmal mit einem noch nicht
 * bestaetigten Jahr los.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { isAxiosError } from 'axios';
import { Alert, Anchor, Card, Group, Select, Stack, Title } from '@mantine/core';
import { PageHeader } from '../../components/ui/PageHeader';
import { SkeletonTable } from '../../components/evaluations/SkeletonTable';
import { TransferFilter } from '../../components/transfers/TransferFilter';
import { TransferGrid } from '../../components/transfers/TransferGrid';
import { TransferList } from '../../components/transfers/TransferList';
import { TransferDetailDrawer } from '../../components/transfers/TransferDetailDrawer';
import { statistikPortalTransferApi } from '../../services/statistikPortalTransferApi';
import { getApiErrorMessage } from '../../utils/errorHandling';
import {
  buildTransferOverviewSearch,
  parseTransferOverviewSearch,
} from './transferOverviewUrl';
import type {
  TransferArea,
  TransferOverviewQuery,
  TransferOverviewResponse,
  TransferListResponse,
} from '../../types/statistikPortalTransfer';

/** Text aus der Plan-Behavior-Zeile (D-21) -- keine eigene Formulierung. */
const FORBIDDEN_MESSAGE =
  'Diese Auswertung gehört zu einem Bereich, für den Sie keine Leseberechtigung haben.';

interface ErrorInfo {
  forbidden: boolean;
  message: string;
}

function toErrorInfo(err: unknown): ErrorInfo {
  if (isAxiosError(err) && err.response?.status === 403) {
    return { forbidden: true, message: FORBIDDEN_MESSAGE };
  }
  return {
    forbidden: false,
    message: getApiErrorMessage(err, 'Die Übertragungen konnten nicht geladen werden.'),
  };
}

export function TransferOverviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = parseTransferOverviewSearch(searchParams);
  const hasYearParam = searchParams.get('year') !== null;

  const [overview, setOverview] = useState<TransferOverviewResponse | null>(null);
  const [list, setList] = useState<TransferListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // Getrennt je Ladevorgang: ein gemeinsamer Zustand liess den Listen-Effekt einen Fehler der
  // Uebersicht loeschen (Seite dann leer) und einen Listenfehler das geladene Raster ausblenden.
  const [overviewError, setOverviewError] = useState<ErrorInfo | null>(null);
  const [listError, setListError] = useState<ErrorInfo | null>(null);
  const [openEntryId, setOpenEntryId] = useState<number | null>(null);

  function updateQuery(
    patch: Partial<TransferOverviewQuery>,
    options?: { replace?: boolean }
  ) {
    setSearchParams(buildTransferOverviewSearch({ ...query, ...patch }), {
      replace: options?.replace ?? false,
    });
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setOverviewError(null);

    statistikPortalTransferApi
      .getOverview({ year: query.year, area: query.area, type: query.type })
      .then((result) => {
        if (cancelled) return;
        setOverview(result);
        if (!hasYearParam) {
          updateQuery({ year: result.year }, { replace: true });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setOverview(null);
        setOverviewError(toErrorInfo(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.year, query.area, query.type]);

  useEffect(() => {
    if (!hasYearParam) {
      setList(null);
      return;
    }

    let cancelled = false;
    setListError(null);

    statistikPortalTransferApi
      .getEntries(query)
      .then((result) => {
        if (!cancelled) setList(result);
      })
      .catch((err) => {
        if (!cancelled) {
          setList(null);
          setListError(toErrorInfo(err));
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasYearParam, query.year, query.area, query.type, query.cellType, query.cellPeriod]);

  function handleAreaChange(area: TransferArea | null) {
    updateQuery({ area, type: null, cellType: null, cellPeriod: null });
  }

  function handleTypeChange(type: string | null) {
    updateQuery({ type, cellType: null, cellPeriod: null });
  }

  function handleYearChange(year: number) {
    updateQuery({ year, cellType: null, cellPeriod: null });
  }

  function handleSelectCell(selection: { evaluationType: string; period: number }) {
    updateQuery({ cellType: selection.evaluationType, cellPeriod: selection.period });
  }

  function handleClearCell() {
    updateQuery({ cellType: null, cellPeriod: null });
  }

  const selectedCell =
    query.cellType !== null && query.cellPeriod !== null
      ? { evaluationType: query.cellType, period: query.cellPeriod }
      : null;

  const showFilterAndGrid =
    overview !== null &&
    (overview.totalCount > 0 || query.area !== null || query.type !== null);

  return (
    <Stack gap="lg">
      <PageHeader
        title="Übertragungen"
        description="Stand je Auswertung und Zeitraum auf einen Blick, darunter jeder einzelne Versuch."
        rightSection={
          overview ? (
            <Select
              label="Jahr"
              w={110}
              data={overview.availableYears.map((year) => String(year))}
              value={String(query.year)}
              allowDeselect={false}
              onChange={(value) => {
                if (value) handleYearChange(Number(value));
              }}
            />
          ) : undefined
        }
      />

      {overviewError && <ErrorAlert error={overviewError} />}

      {loading && !overviewError && <SkeletonTable rows={8} columns={4} />}

      {!loading && !overviewError && overview && (
        <>
          {showFilterAndGrid && (
            <>
              <TransferFilter
                areas={overview.areas}
                area={overview.area}
                evaluationTypes={overview.evaluationTypes}
                type={overview.type}
                onAreaChange={handleAreaChange}
                onTypeChange={handleTypeChange}
              />
              <Card withBorder radius="md" p={16}>
                <TransferGrid
                  groups={overview.grid}
                  selectedCell={selectedCell}
                  onSelectCell={handleSelectCell}
                />
              </Card>
            </>
          )}

          {listError && <ErrorAlert error={listError} />}

          {list && (
            <>
              <Group justify="space-between" align="center">
                <Title order={2} fz={18}>
                  {list.title}
                </Title>
                {list.scoped && (
                  <Anchor component="button" type="button" onClick={handleClearCell}>
                    alle Einträge zeigen
                  </Anchor>
                )}
              </Group>
              <TransferList
                entries={list.entries}
                scoped={list.scoped}
                selectedId={openEntryId}
                onSelect={setOpenEntryId}
              />
            </>
          )}
        </>
      )}

      <TransferDetailDrawer entryId={openEntryId} onClose={() => setOpenEntryId(null)} />
    </Stack>
  );
}

function ErrorAlert({ error }: { error: ErrorInfo }) {
  return (
    <Alert color={error.forbidden ? 'gray' : 'red'} title={error.forbidden ? 'Keine Berechtigung' : 'Fehler'}>
      {error.message}
    </Alert>
  );
}
